import React, { useState, useEffect } from "react";
import { X, ChevronRight, Shield, Zap } from "lucide-react";
import { useQuote } from "./QuoteContext";

const ASSURANCE_PRODUCTS = [
  { v: "auto", l: "Auto" },
  { v: "habitation", l: "Habitation" },
  { v: "sante", l: "Mutuelle Santé" },
  { v: "rc-pro", l: "RC Pro" },
  { v: "emprunteur", l: "Emprunteur" },
  { v: "per", l: "PER" },
  { v: "prevoyance", l: "Prévoyance" },
];

const ENERGY_TYPES = [
  { v: "electricite", l: "Électricité" },
  { v: "gaz", l: "Gaz" },
  { v: "les_deux", l: "Les deux" },
];

const ENERGY_PROVIDERS = [
  { v: "edf", l: "EDF" },
  { v: "engie", l: "Engie" },
  { v: "total", l: "TotalEnergies" },
  { v: "autre", l: "Autre" },
];

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-brand-ink/60 mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

function Select({ value, onChange, options, placeholder, testid }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full appearance-none bg-white border border-brand-border rounded-full px-4 py-2.5 text-sm text-brand-ink focus:outline-none focus:border-brand-ink transition-colors cursor-pointer"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.v} value={o.v}>
          {o.l}
        </option>
      ))}
    </select>
  );
}

export default function CompareContractsModal({ open, onClose }) {
  const { openQuote } = useQuote();
  const [assuranceProduct, setAssuranceProduct] = useState("");
  const [profile, setProfile] = useState("");
  const [energyType, setEnergyType] = useState("");
  const [provider, setProvider] = useState("");
  const [bill, setBill] = useState(200);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const onCompareAssurance = () => {
    onClose();
    openQuote(assuranceProduct || "assurance", { profil: profile });
  };

  const onCalcEnergy = () => {
    onClose();
    openQuote("energie", {
      energy_type: energyType,
      current_provider: provider,
      monthly_bill: bill,
    });
  };

  return (
    <div
      data-testid="compare-modal"
      className="fixed inset-0 z-[70] items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200 hidden lg:flex"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="relative w-full max-w-5xl bg-white rounded-3xl shadow-[0_40px_100px_rgba(0,0,0,0.35)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="compare-modal-close"
          onClick={onClose}
          className="absolute top-5 right-5 h-10 w-10 rounded-full bg-brand-sand-2 hover:bg-brand-sand text-brand-ink flex items-center justify-center transition-colors"
          aria-label="Fermer"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="px-10 pt-10 pb-6 text-center">
          <div className="overline text-brand-ink/55">Bilan personnalisé</div>
          <h3 className="mt-3 font-serif text-[34px] md:text-[40px] leading-[1.05] tracking-tight text-brand-ink">
            Votre Bilan{" "}
            <span className="italic font-editorial">d'Économies Personnalisé</span>
          </h3>
        </div>

        <div className="px-10 pb-8 grid grid-cols-2 gap-6">
          {/* ASSURANCE */}
          <section
            data-testid="compare-modal-assurance"
            className="rounded-2xl bg-brand-sand-2 p-7 flex flex-col"
          >
            <div className="flex items-center gap-2 mb-5">
              <div className="h-8 w-8 rounded-full bg-brand-ink text-white flex items-center justify-center">
                <Shield className="h-3.5 w-3.5" />
              </div>
              <span className="text-[11px] uppercase tracking-[0.25em] font-semibold text-brand-ink">
                Assurance
              </span>
            </div>

            <div className="space-y-4 flex-1">
              <Field label="Quel type d'assurance ?">
                <Select
                  testid="compare-modal-product"
                  value={assuranceProduct}
                  onChange={setAssuranceProduct}
                  options={ASSURANCE_PRODUCTS}
                  placeholder="Sélectionner"
                />
              </Field>
              <Field label="Votre profil">
                <Select
                  testid="compare-modal-profile"
                  value={profile}
                  onChange={setProfile}
                  options={[
                    { v: "particulier", l: "Particulier" },
                    { v: "professionnel", l: "Professionnel" },
                  ]}
                  placeholder="Sélectionner"
                />
              </Field>
            </div>

            <button
              data-testid="compare-modal-compare-btn"
              onClick={onCompareAssurance}
              className="btn-press mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-ink hover:bg-black text-white px-5 py-3.5 text-[12px] font-semibold tracking-[0.2em] uppercase"
            >
              Comparer mes offres <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </section>

          {/* ÉNERGIE */}
          <section
            data-testid="compare-modal-energie"
            className="rounded-2xl bg-brand-sand-2 p-7 flex flex-col"
          >
            <div className="flex items-center gap-2 mb-5">
              <div className="h-8 w-8 rounded-full bg-brand-ink text-white flex items-center justify-center">
                <Zap className="h-3.5 w-3.5" />
              </div>
              <span className="text-[11px] uppercase tracking-[0.25em] font-semibold text-brand-ink">
                Énergie
              </span>
            </div>

            <div className="space-y-4 flex-1">
              <Field label="Type d'énergie">
                <Select
                  testid="compare-modal-energy-type"
                  value={energyType}
                  onChange={setEnergyType}
                  options={ENERGY_TYPES}
                  placeholder="Sélectionner"
                />
              </Field>
              <Field label="Fournisseur actuel">
                <Select
                  testid="compare-modal-provider"
                  value={provider}
                  onChange={setProvider}
                  options={ENERGY_PROVIDERS}
                  placeholder="Sélectionner"
                />
              </Field>
              <Field label={`Facture mensuelle — ${bill} €`}>
                <input
                  data-testid="compare-modal-bill"
                  type="range"
                  min={50}
                  max={2000}
                  step={10}
                  value={bill}
                  onChange={(e) => setBill(Number(e.target.value))}
                  className="w-full accent-brand-ink"
                />
                <div className="flex justify-between text-[10px] text-brand-ink/55 mt-1">
                  <span>50 €</span>
                  <span>2 000 €</span>
                </div>
              </Field>
            </div>

            <button
              data-testid="compare-modal-calc-btn"
              onClick={onCalcEnergy}
              className="btn-press mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-ink hover:bg-black text-white px-5 py-3.5 text-[12px] font-semibold tracking-[0.2em] uppercase"
            >
              Calculer mes économies <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </section>
        </div>

        <div
          data-testid="compare-modal-footer"
          className="border-t border-brand-border bg-brand-sand-2/50 px-10 py-5 text-center text-[11px] uppercase tracking-[0.25em] text-brand-ink/65"
        >
          Sans frais · Sans engagement · Réponse sous&nbsp;24&nbsp;h
        </div>
      </div>
    </div>
  );
}
